import {
  adminService,
  type ActivityPromoRead,
  type PromoMarginMode,
  type PromoPreviewRequest,
} from "@/services/adminService";

export type SavedItemPromo = Pick<ActivityPromoRead, "id"> & {
  activity_item_id: string;
};

export type PromoUpsert = {
  activity_item_id: string | null;
  margin_mode: PromoMarginMode;
  margin_value: number;
  is_active: boolean;
};

export type PromoSaveOperation =
  { kind: "upsert"; promo: PromoUpsert } | { kind: "delete"; promoId: string };

/**
 * Requests that persist the promo form, in order: the activity-level promo,
 * each item override (always active), then the deletion of saved overrides
 * the admin removed.
 */
export function planPromoSave(
  promo: Required<PromoPreviewRequest>,
  isPromoActive: boolean,
  savedItemPromos: SavedItemPromo[],
): PromoSaveOperation[] {
  const keptItemIds = new Set(
    promo.item_overrides.map((override) => override.activity_item_id),
  );
  return [
    {
      kind: "upsert",
      promo: {
        activity_item_id: null,
        margin_mode: promo.margin_mode,
        margin_value: promo.margin_value,
        is_active: isPromoActive,
      },
    },
    ...promo.item_overrides.map((override): PromoSaveOperation => ({
      kind: "upsert",
      promo: { ...override, is_active: true },
    })),
    ...savedItemPromos
      .filter((saved) => !keptItemIds.has(saved.activity_item_id))
      .map((saved): PromoSaveOperation => ({
        kind: "delete",
        promoId: saved.id,
      })),
  ];
}

/** Runs the planned requests one at a time, stopping at the first failure. */
export async function runPromoSave(
  activityId: string,
  operations: PromoSaveOperation[],
): Promise<void> {
  for (const operation of operations) {
    if (operation.kind === "upsert") {
      await adminService.upsertActivityPromo({
        activityId,
        ...operation.promo,
      });
    } else {
      await adminService.deleteActivityPromo(activityId, operation.promoId);
    }
  }
}
